import { useEffect, useState } from "react";
import { Card, Table } from "react-bootstrap";
import { useGetVaBillsQuery } from "../../app/vaApi/vaApi";
import CustomDropdown from "../../components/shared/dropdown/CustomDropdown";
import Button from "../../components/shared/buttons/button";
import Loader from "../../components/shared/loader/Loader";
import Pagination from "../../components/shared/pagination/Pagination";
import { toBillStatusParam } from "../../helpers/vaSettingsUtils";
import "./vaDashboardSettings.css";

const BILL_TABS = ["All", "Paid", "Pending", "Failed", "Cancelled"];
const BILL_TYPE_OPTIONS = [
  { value: "All", label: "All" },
  { value: "Order Revenue", label: "Order Revenue" },
  { value: "Commission Payout", label: "Commission Payout" },
  { value: "Order Cancelled", label: "Order Cancelled" },
];
const BILLS_PAGE_SIZE = 10;

export default function VaDashboardTransactions() {
  const [billTab, setBillTab] = useState("All");
  const [billTypeTab, setBillTypeTab] = useState("All");
  const [billPage, setBillPage] = useState(1);

  const billStatus = toBillStatusParam(billTab);
  const {
    data: billsData,
    isLoading: billsLoading,
    isError: billsError,
    refetch: refetchBills,
  } = useGetVaBillsQuery(
    {
      status: billStatus,
      type: billTypeTab,
      page: billPage,
      limit: BILLS_PAGE_SIZE,
    },
    { refetchOnMountOrArgChange: true }
  );

  const bills = billsData?.data?.items ?? [];
  const pagination = billsData?.data?.pagination;
  const totalPages = pagination?.totalPages || 0;
  const totalItems = Number(pagination?.total) || 0;
  const pageLimit = Number(pagination?.limit) || BILLS_PAGE_SIZE;

  useEffect(() => {
    setBillPage(1);
  }, [billTab, billTypeTab]);

  const billTabClass = (tab) =>
    `va-dashboard-settings__tab-btn ${billTab === tab ? "active" : ""}`;

  return (
    <div className="va-dashboard-settings">
      <div className="va-dashboard-settings__header">
        <h4 className="va-dashboard-settings__title">Transactions</h4>
        <p className="va-dashboard-settings__payout-note mb-0">
          Monthly payouts on the 15th. Includes shipped orders from the
          previous month
        </p>
      </div>

      <Card className="va-dashboard-settings__card">
        <Card.Body>
          <p className="va-dashboard-settings__card-subtitle text-muted small mb-3">
            Order revenue you brought in and commission payouts from Captify.
          </p>

          <div className="va-dashboard-settings__filters">
            <div className="va-dashboard-settings__tabs" role="tablist">
              {BILL_TABS.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  className={billTabClass(tab)}
                  onClick={() => setBillTab(tab)}
                  role="tab"
                  aria-selected={billTab === tab}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="va-dashboard-settings__filter-type">
              <CustomDropdown
                label="Type"
                value={billTypeTab}
                onSelect={(opt) => setBillTypeTab(opt.value)}
                options={BILL_TYPE_OPTIONS}
                className="w-100"
              />
            </div>
          </div>

          <div className="va-dashboard-settings__table-wrap">
            {billsLoading ? (
              <div className="py-4 d-flex justify-content-center">
                <Loader />
              </div>
            ) : billsError ? (
              <div className="py-3 text-center">
                <p className="text-danger small mb-2">Failed to load transactions</p>
                <Button label="Retry" className="btn-grey" onClick={refetchBills} />
              </div>
            ) : (
              <Table hover responsive className="va-dashboard-settings__table">
                <thead>
                  <tr>
                    <th>Bill Number</th>
                    <th>Date</th>
                    <th>Reason</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bills.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-muted text-center py-4">
                        No transactions found
                      </td>
                    </tr>
                  ) : (
                    bills.map((bill) => {
                      const typeClass = bill.type
                        ? ` va-dashboard-settings__row--${String(bill.type)
                            .toLowerCase()
                            .replace(/\s+/g, "-")}`
                        : "";
                      const amountClass =
                        bill.amountRaw < 0
                          ? "va-dashboard-settings__amount--negative"
                          : "va-dashboard-settings__amount--positive";
                      return (
                        <tr
                          key={bill.id || bill.orderNumber || bill.billNumber}
                          className={typeClass.trim() || undefined}
                        >
                          <td>{bill.billNumber || bill.number || "—"}</td>
                          <td>{bill.date}</td>
                          <td>
                            <div className="va-dashboard-settings__reason">
                              <span className="va-dashboard-settings__reason-title">
                                {bill.reasonTitle || bill.reason || bill.billReason}
                              </span>
                              {bill.reasonPeriod ? (
                                <span className="va-dashboard-settings__reason-period">
                                  ({bill.reasonPeriod})
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td className={amountClass}>{bill.amount}</td>
                          <td>
                            <span
                              className={`va-dashboard-settings__status-badge va-dashboard-settings__status-badge--${String(
                                bill.status || ""
                              ).toLowerCase()}`}
                            >
                              {bill.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            )}
          </div>

          {(totalItems > 0 || totalPages > 1) && (
            <div className="va-dashboard-settings__pagination">
              <div>
                {totalItems > 0 && (
                  <span>
                    Showing{" "}
                    <strong>
                      {(billPage - 1) * pageLimit + 1} –{" "}
                      {Math.min(billPage * pageLimit, totalItems)}
                    </strong>{" "}
                    of <strong>{totalItems}</strong>
                  </span>
                )}
              </div>
              {totalPages > 1 && (
                <Pagination
                  totalPages={totalPages}
                  currentPage={billPage}
                  onPageChange={setBillPage}
                />
              )}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
